import { Fragment } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DashboardStats } from "@/lib/api/dashboard.ts";

interface Stats {
  data: DashboardStats;
}

function formatNumber(num: number): string {
  if (num >= 10000) {
    return `${Math.round(num / 1000)}k`;
  }
  return num.toString();
}

export function Stats({ data }: Stats) {
  const stats = [
    {
      title: "Average Rating",
      value: `${data.averageRating ? data.averageRating.toFixed(2) + " stars" : "-"}`,
      tooltip:
        "Average mentor rating from post-session and follow-up feedback (1–5 stars) for the selected timeframe.",
    },
    {
      title: "Avg Response Time (All Hours)",
      value: `${data.averageResponseTime ? data.averageResponseTime.toFixed(2) + " mins" : "-"}`,
      tooltip:
        "Message-weighted average minutes between a teen message and the mentor's next reply, regardless of when the message was sent.",
    },
    {
      title: "Avg Response Time (Scheduled)",
      value: `${data.scheduledAverageResponseTime ? data.scheduledAverageResponseTime.toFixed(2) + " mins" : "-"}`,
      tooltip:
        "Message-weighted average response time for teen messages that arrived during the mentor's scheduled hours (custom schedule or default 4–10 pm ET, Mon–Fri).",
    },
    {
      title: "Teen Days Engaged",
      value: `${data.teenEngagement} days`,
      tooltip:
        "Total number of teen-day interactions (unique teen × day combinations with activity) during the selected period.",
    },
    {
      title: "Teen messages",
      value: `${formatNumber(data.teenMessagesSent)} messages`,
      tooltip:
        "Total count of messages sent by teens across all mentorships in the selected timeframe.",
    },
    {
      title: "Total active teens",
      value: `${data.totalActiveTeens}`,
      tooltip:
        "Number of teens with an active mentorship assignment right now (regardless of recent activity).",
    },
    {
      title: "Total Engaged Teens",
      value: `${data.totalEngagedTeens}`,
      tooltip:
        "Teens who had at least two distinct message days in the selected timeframe.",
    },
    {
      title: "Total Billable Teens",
      value: `${data.totalBillableTeens}`,
      tooltip:
        "Teens currently counted toward billing for the month, based on mentorship assignment and program rules. A teen does not need to remain active all month to be billable—teens who were active at any point in the month may still be included.",
    },
  ];

  return (
    <div className="inline-flex h-24 w-full items-center justify-start rounded-xl border border-neutral-200">
      {stats.map((stat, index) => (
        <Fragment key={index}>
          <TooltipProvider delayDuration={150} key={`provider-${index}`}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="inline-flex shrink grow basis-0 cursor-help flex-col items-center justify-center gap-2 p-3">
                  <div className="self-stretch text-center text-xs font-normal leading-tight text-black">
                    {stat.title}
                  </div>
                  <div className="text-center text-base font-bold leading-tight text-black">
                    {stat.value}
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="max-w-xs text-center text-xs"
              >
                {stat.tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {index !== stats.length - 1 && (
            <div className="flex w-px items-start justify-start self-stretch">
              <div className="relative h-24 w-px rounded-full bg-neutral-200" />
            </div>
          )}
        </Fragment>
      ))}
    </div>
  );
}
